import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';
import { useLanguageStore } from '@/modules/core';

import {
  dialogOverlayVariants,
  dialogContentVariants,
  dialogHeaderVariants,
  dialogTitleVariants,
  dialogDescriptionVariants,
  dialogBodyVariants,
  dialogFooterVariants,
  dialogCloseButtonVariants,
  dialogFooterStartVariants,
  dialogFooterEndVariants,
  dialogIconSizeVariants,
} from './Dialog.styles';

import type {
  DialogProps,
  DialogTriggerProps,
  DialogOverlayProps,
  DialogContentProps,
  DialogHeaderProps,
  DialogTitleProps,
  DialogDescriptionProps,
  DialogBodyProps,
  DialogFooterProps,
  DialogCloseProps,
  DialogScale,
} from './Dialog.types';

/**
 * Dialog Root Component
 *
 * Container for all dialog parts. Manages open/close state.
 * Use this to wrap trigger and content.
 *
 * @example
 * ```tsx
 * <Dialog open={isOpen} onOpenChange={setIsOpen}>
 *   <DialogTrigger>Open</DialogTrigger>
 *   <DialogContent>...</DialogContent>
 * </Dialog>
 * ```
 */
export const Dialog: React.FC<DialogProps> = ({ children, ...props }) => {
  const { isRTL } = useLanguageStore();
  const Component = DialogPrimitive.Root as React.ComponentType<
    DialogProps & { dir?: 'ltr' | 'rtl' }
  >;
  return (
    <Component dir={isRTL ? 'rtl' : 'ltr'} {...props}>
      {children}
    </Component>
  );
};

Dialog.displayName = 'Dialog';

/**
 * Dialog Trigger Component
 *
 * Button that opens the dialog when clicked.
 */
export const DialogTrigger = React.forwardRef<HTMLButtonElement, DialogTriggerProps>(
  ({ children, ...props }, ref) => (
    <DialogPrimitive.Trigger ref={ref} {...props}>
      {children}
    </DialogPrimitive.Trigger>
  )
);

DialogTrigger.displayName = 'DialogTrigger';

/**
 * Dialog Overlay Component
 *
 * Semi-transparent backdrop behind the dialog.
 * Automatically rendered by DialogContent.
 */
export const DialogOverlay = React.forwardRef<HTMLDivElement, DialogOverlayProps>(
  ({ className, ...props }, ref) => (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(dialogOverlayVariants(), className)}
      {...props}
    />
  )
);

DialogOverlay.displayName = 'DialogOverlay';

/**
 * Dialog Portal Component
 *
 * Renders dialog in a portal (outside DOM hierarchy).
 * Automatically used by DialogContent.
 */
export const DialogPortal = DialogPrimitive.Portal;

/**
 * Dialog Close Component
 *
 * Button that closes the dialog.
 * Can be used anywhere inside DialogContent.
 */
export const DialogClose = React.forwardRef<HTMLButtonElement, DialogCloseProps>(
  ({ className, scale = 'm', 'aria-label': ariaLabel = 'Close dialog', ...props }, ref) => {
    const iconSize = dialogIconSizeVariants[scale as 's' | 'm' | 'l'];
    return (
      <DialogPrimitive.Close
        ref={ref}
        className={cn(dialogCloseButtonVariants({ scale }), className)}
        aria-label={ariaLabel}
        {...props}
      >
        <X className={cn(iconSize)} />
      </DialogPrimitive.Close>
    );
  }
);

DialogClose.displayName = 'DialogClose';

/**
 * Dialog Content Component
 *
 * Main container for dialog content.
 * Includes overlay, animations, and focus trap.
 *
 * @example
 * ```tsx
 * <DialogContent scale="m" width="m">
 *   <DialogHeader>
 *     <DialogTitle>Dialog Title</DialogTitle>
 *   </DialogHeader>
 *   <DialogBody>Content here</DialogBody>
 *   <DialogFooter>Actions here</DialogFooter>
 * </DialogContent>
 * ```
 */
export const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(
  (
    {
      className,
      children,
      scale = 'm',
      width = 'm',
      showClose = true,
      closeLabel = 'Close dialog',
      overlayClassName,
      ...props
    },
    ref
  ) => (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ scale, width }), className)}
        {...props}
      >
        {children}
        {showClose && (
          <DialogPrimitive.Close
            className={cn(dialogCloseButtonVariants({ scale }), 'absolute end-2 top-2')}
            aria-label={closeLabel}
          >
            <X className={cn(dialogIconSizeVariants[scale as 's' | 'm' | 'l'])} />
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  )
);

DialogContent.displayName = 'DialogContent';

/**
 * Dialog Header Component
 *
 * Container for dialog title and optional close button.
 * Place at the top of DialogContent.
 */
export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
  ({ className, scale = 'm', children, ...props }, ref) => (
    <div ref={ref} className={cn(dialogHeaderVariants({ scale }), className)} {...props}>
      {children}
    </div>
  )
);

DialogHeader.displayName = 'DialogHeader';

/**
 * Dialog Title Component
 *
 * Accessible title for the dialog.
 * Required for accessibility - use inside DialogHeader.
 */
export const DialogTitle = React.forwardRef<HTMLHeadingElement, DialogTitleProps>(
  ({ className, scale = 'm', children, ...props }, ref) => (
    <DialogPrimitive.Title
      ref={ref}
      className={cn(dialogTitleVariants({ scale }), className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Title>
  )
);

DialogTitle.displayName = 'DialogTitle';

/**
 * Dialog Description Component
 *
 * Optional description for accessibility.
 * Use inside DialogHeader or DialogBody.
 */
export const DialogDescription = React.forwardRef<HTMLParagraphElement, DialogDescriptionProps>(
  ({ className, children, ...props }, ref) => (
    <DialogPrimitive.Description
      ref={ref}
      className={cn(dialogDescriptionVariants(), className)}
      {...props}
    >
      {children}
    </DialogPrimitive.Description>
  )
);

DialogDescription.displayName = 'DialogDescription';

/**
 * Dialog Body Component
 *
 * Scrollable content area of the dialog.
 * Place between DialogHeader and DialogFooter.
 */
export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, scale = 'm', children, ...props }, ref) => (
    <div ref={ref} className={cn(dialogBodyVariants({ scale }), className)} {...props}>
      {children}
    </div>
  )
);

DialogBody.displayName = 'DialogBody';

/**
 * Dialog Footer Component
 *
 * Container for action buttons at the bottom of the dialog.
 * Automatically handles left/right button alignment.
 */
export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, scale = 'm', children, ...props }, ref) => (
    <div ref={ref} className={cn(dialogFooterVariants({ scale }), className)} {...props}>
      {children}
    </div>
  )
);

DialogFooter.displayName = 'DialogFooter';

/**
 * Dialog Footer Start Component
 *
 * Left-aligned section of footer.
 * Use for secondary/cancel actions.
 */
export const DialogFooterStart: React.FC<{
  scale?: DialogScale;
  children?: React.ReactNode;
  className?: string;
}> = ({ scale = 'm', children, className }) => (
  <div className={cn(dialogFooterStartVariants({ scale }), className)}>{children}</div>
);

DialogFooterStart.displayName = 'DialogFooterStart';

/**
 * Dialog Footer End Component
 *
 * Right-aligned section of footer.
 * Use for primary actions (confirm, save, etc.).
 */
export const DialogFooterEnd: React.FC<{
  scale?: DialogScale;
  children?: React.ReactNode;
  className?: string;
}> = ({ scale = 'm', children, className }) => (
  <div className={cn(dialogFooterEndVariants({ scale }), className)}>{children}</div>
);

DialogFooterEnd.displayName = 'DialogFooterEnd';
